// Notification severity configuration for visual hierarchy
import { NotificationType } from '@/types/notifications';

export type SeverityLevel = 'high' | 'medium' | 'low';

interface SeverityConfig {
  level: SeverityLevel;
  borderWidth: string;
  bgOpacity: string;
  animate: boolean;
  category: NotificationCategory;
}

export type NotificationCategory = 'connections' | 'messages' | 'profile' | 'system' | 'all';

// Map notification types to categories
export const NOTIFICATION_CATEGORIES: Record<NotificationType, NotificationCategory> = {
  match_created: 'connections',
  like_received: 'connections',
  connection_removed: 'connections',
  new_message: 'messages',
  profile_view: 'profile',
  success: 'system',
  error: 'system',
  warning: 'system',
  info: 'system',
  system_error: 'system',
  system_announcement: 'system',
};

// Severity configurations per notification type
export const NOTIFICATION_SEVERITY: Record<NotificationType, SeverityConfig> = {
  // High attention - critical notifications
  match_created: {
    level: 'high',
    borderWidth: 'w-1.5',
    bgOpacity: 'bg-white/15',
    animate: true,
    category: 'connections',
  },
  new_message: {
    level: 'high',
    borderWidth: 'w-1.5',
    bgOpacity: 'bg-white/15',
    animate: true,
    category: 'messages',
  },
  error: {
    level: 'high',
    borderWidth: 'w-1.5',
    bgOpacity: 'bg-red-500/10',
    animate: true,
    category: 'system',
  },
  system_error: {
    level: 'high',
    borderWidth: 'w-1.5',
    bgOpacity: 'bg-red-500/10',
    animate: true,
    category: 'system',
  },

  // Medium attention - important but not urgent
  like_received: {
    level: 'medium',
    borderWidth: 'w-1',
    bgOpacity: 'bg-white/10',
    animate: false,
    category: 'connections',
  },
  connection_removed: {
    level: 'medium',
    borderWidth: 'w-1',
    bgOpacity: 'bg-white/10',
    animate: false,
    category: 'connections',
  },
  warning: {
    level: 'medium',
    borderWidth: 'w-1',
    bgOpacity: 'bg-amber-500/10',
    animate: false,
    category: 'system',
  },

  // Low attention - informational
  profile_view: {
    level: 'low',
    borderWidth: 'w-0.5',
    bgOpacity: 'bg-white/5',
    animate: false,
    category: 'profile',
  },
  info: {
    level: 'low',
    borderWidth: 'w-0.5',
    bgOpacity: 'bg-white/5',
    animate: false,
    category: 'system',
  },
  success: {
    level: 'low',
    borderWidth: 'w-0.5',
    bgOpacity: 'bg-green-500/5',
    animate: false,
    category: 'system',
  },
  system_announcement: {
    level: 'low',
    borderWidth: 'w-0.5',
    bgOpacity: 'bg-white/5',
    animate: false,
    category: 'system',
  },
};

export function getNotificationSeverity(type: NotificationType): SeverityConfig {
  return NOTIFICATION_SEVERITY[type] || NOTIFICATION_SEVERITY.info;
}

export function getNotificationCategory(type: NotificationType): NotificationCategory {
  return NOTIFICATION_CATEGORIES[type] || 'system';
}

// Category display info
export const CATEGORY_INFO: Record<NotificationCategory, { label: string; icon: string }> = {
  all: { label: 'Todas', icon: '📬' },
  connections: { label: 'Conexões', icon: '💖' },
  messages: { label: 'Mensagens', icon: '💬' },
  profile: { label: 'Perfil', icon: '👤' },
  system: { label: 'Sistema', icon: '⚙️' },
};
